import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import KontaktFeld from "./KontaktFeld";
import {
  newBeteiligtenId,
  type BeteiligtenEintrag,
  type BeteiligtenFeld,
} from "./beteiligteTypes";
import type { Ansprache } from "./texte";

/**
 * Repeater für die 1:n-Rollen am Objekt — Mieter, Pächter, Miteigentümer,
 * sonstige Nutzer, weitere verbundene Personen/Unternehmen.
 *
 * `forms-core` hat bewusst keinen generischen Repeater: `FieldDef` kennt kein
 * Kindfeld-Konzept, und `personEntries`/`verantEntries` haben fest verdrahtete
 * Feldsätze. Deshalb hier ein eigener, konfigurierbarer Repeater im Aufbau von
 * `EinheitenList`.
 *
 * Die gesamte Liste liegt unter **einem** Answer-Key. Grund: `PUT
 * /risk-objects/:id` ist ein Replace-Set und die Detailansicht ordnet Merkmale
 * per Regex über den Key-Präfix zu — pro Eintrag eigene Keys würden beides
 * brechen.
 *
 * `von`/`bis` sind echte Datumsfelder (Spec Z. 42/46). Freitext-Daten im
 * schemalosen Merkmalsspeicher wären später nicht auswertbar; leeres `bis`
 * bleibt zulässig (unbefristete Verträge).
 */

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "12px" },
  intro: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.muted,
  },
  card: {
    backgroundColor: BRAND.white,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  head: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    fontFamily: BRAND.fontAccent,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  removeBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.muted,
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    fontWeight: 700,
    width: "26px",
    height: "26px",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: "#f5eef6", color: BRAND.purple },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 640px)": { gridTemplateColumns: "1fr 1fr" },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    minWidth: 0,
  },
  fullSpan: {
    "@media (min-width: 640px)": { gridColumn: "1 / -1" },
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":focus": { ...shorthands.borderColor(BRAND.purple) },
  },
  addBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 700,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "dashed", BRAND.purple),
    ...shorthands.borderRadius("6px"),
    alignSelf: "flex-start",
    ":hover": { backgroundColor: "#f5eef6" },
  },
  empty: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontStyle: "italic",
    color: BRAND.muted,
  },
});

const DEFAULT_LABELS: Record<BeteiligtenFeld, string> = {
  einheit: "Betroffene Einheit / Fläche",
  von: "Beginn",
  bis: "Ende (leer = unbefristet)",
  art: "Art",
  anteilProzent: "Anteil (%)",
  bemerkung: "Bemerkung",
};

const INPUT_TYPE: Record<BeteiligtenFeld, string> = {
  einheit: "text",
  von: "date",
  bis: "date",
  art: "text",
  anteilProzent: "number",
  bemerkung: "text",
};

type Props = {
  /** Antwort-Key, unter dem die gesamte Liste liegt. */
  listenKey: string;
  entries: BeteiligtenEintrag[];
  onChange: (next: BeteiligtenEintrag[]) => void;
  /** Singular-Bezeichnung, z. B. „Mieter". */
  singular: string;
  /** Überschrift des Kontakt-Dialogs, z. B. „Mieter auswählen". */
  dialogTitle: string;
  /** Zusatzfelder je Eintrag, in dieser Reihenfolge. */
  felder: BeteiligtenFeld[];
  /** Abweichende Feldbeschriftungen. */
  labels?: Partial<Record<BeteiligtenFeld, string>>;
  lookup: boolean;
  ansprache: Ansprache;
  intro?: string;
};

const BeteiligtenListe = ({
  listenKey,
  entries,
  onChange,
  singular,
  dialogTitle,
  felder,
  labels,
  lookup,
  ansprache,
  intro,
}: Props) => {
  const styles = useStyles();

  const update = (id: string, patch: Partial<BeteiligtenEintrag>) =>
    onChange(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const remove = (id: string) => onChange(entries.filter((e) => e.id !== id));

  const add = () =>
    onChange([...entries, { id: newBeteiligtenId(), name: "" }]);

  return (
    <div className={styles.root}>
      {intro && <span className={styles.intro}>{intro}</span>}
      {entries.length === 0 && (
        <span className={styles.empty}>Noch kein Eintrag erfasst.</span>
      )}
      {entries.map((entry, idx) => (
        <div key={entry.id} className={styles.card}>
          <div className={styles.head}>
            <span className={styles.badge}>{idx + 1}</span>
            <span className={styles.title}>
              {entry.name || `${singular} ${idx + 1}`}
            </span>
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => remove(entry.id)}
              aria-label={`${singular} entfernen`}
            >
              ×
            </button>
          </div>

          {/* Der Kontakt liegt je Eintrag unter `kontaktRef`/`name`; KontaktFeld
              arbeitet auf einem Answer-Bag, deshalb hier ein Mini-Bag.

              Bewusst `onRefChange` statt `onChange`: `update()` rechnet aus dem
              `entries`-Prop des aktuellen Renders. Zwei getrennte Schreibaufrufe
              (erst `kontaktRef`, dann `name`) würden sich gegenseitig
              überschreiben — der `kontaktRef` ginge still verloren. */}
          <KontaktFeld
            kontaktKey="kontaktRef"
            nameKey="name"
            label={singular}
            dialogTitle={dialogTitle}
            lookup={lookup}
            ansprache={ansprache}
            hideHint
            answers={{ kontaktRef: entry.kontaktRef, name: entry.name }}
            onRefChange={(ref) =>
              update(entry.id, { kontaktRef: ref, name: ref.label })
            }
          />

          {felder.length > 0 && (
            <div className={styles.grid}>
              {felder.map((f) => (
                <div
                  key={f}
                  className={mergeClasses(
                    styles.field,
                    (f === "bemerkung" || f === "einheit") && styles.fullSpan,
                  )}
                >
                  <span className={styles.label}>
                    {labels?.[f] ?? DEFAULT_LABELS[f]}
                  </span>
                  <input
                    type={INPUT_TYPE[f]}
                    name={`${listenKey}-${idx}-${f}`}
                    className={styles.input}
                    value={entry[f] ?? ""}
                    onChange={(e) =>
                      update(entry.id, {
                        [f]: e.target.value,
                      } as Partial<BeteiligtenEintrag>)
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        className={styles.addBtn}
        onClick={add}
      >
        + {singular} hinzufügen
      </button>
    </div>
  );
};

export default BeteiligtenListe;
